import React, { useEffect, useCallback, useMemo, useRef } from 'react'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import EditableTextField from '../../../common/fields/editableTextField'
import OutlinedFieldFormik from '../../../formik/fields/outlinedFieldFormik'
import NumberFieldFormik from '../../../formik/fields/numberFieldFormik'
import SelectFormik from '../../../formik/fields/selectFormik'
import SectionForm from '../../../common/sectionForm'
import BaseAutoComplete from '../../../formik/fields/BaseAutoComplete'
import { getProvinces } from '../../../../utils'
import { t } from 'i18next'
import { useGetCities, useGetCityByPostal } from '../../../../hooks/api/cities'
import { useDebouncedSearch } from '../../../../hooks/useDebouncedSearch'
import { useAddressState } from '../../../../hooks/useAddressState'
import useMediaQuery from '@mui/material/useMediaQuery'

interface Props {
  formik: any
}

const provinceQuebec = 'QC'

const AdressForm = ({ formik }: Props) => {
  const isMobile = useMediaQuery('(max-width:800px)')
  
  const citySearch = useDebouncedSearch({ debounceDelay: 200, minLength: 3 })
  const postalSearch = useDebouncedSearch({ debounceDelay: 200, minLength: 5, maxLength: 7 })
  
  const addressState = useAddressState()
  
  const previousCityIdRef = useRef<string | null>(null)
  
  const currentAddress = useMemo(() => formik.values.address, [formik.values.address])
  const initialAddress = useMemo(() => formik.initialValues.address, [formik.initialValues.address])
  
  const { data: cities } = useGetCities(citySearch.debouncedSearchValue, {
    enabled: Boolean(currentAddress.province === provinceQuebec && citySearch.shouldTriggerSearch),
  })
  
  const { data: postes } = useGetCityByPostal(currentAddress.city_id, postalSearch.debouncedSearchValue, {
    enabled: Boolean(
      currentAddress.city_id && 
      currentAddress.city_id.trim() !== '' &&
      currentAddress.province === provinceQuebec &&
      postalSearch.shouldTriggerSearch
    ),
  })

  useEffect(() => {
    if (!addressState.isInitialized && 
        currentAddress.province && 
        currentAddress.province !== '') {
      addressState.initializeOriginalValues({
        province: currentAddress.province,
        city: currentAddress.city,
        cityId: currentAddress.city_id,
        postalCode: currentAddress.postal_code
      })
      
      if (initialAddress.province === provinceQuebec) {
        citySearch.updateSearch(initialAddress.city || '')
        postalSearch.updateSearch(initialAddress.postal_code || '')
      }
    }
  }, [currentAddress, addressState, initialAddress, citySearch.updateSearch, postalSearch.updateSearch])

  useEffect(() => {
    if (!addressState.isInitialized) return
    
    const { originalValues } = addressState
    const currentProvince = currentAddress.province
    
    const batchUpdateAddress = (updates: Partial<typeof currentAddress>) => {
      formik.setValues((prev: any) => ({
        ...prev,
        address: {
          ...prev.address,
          ...updates
        }
      }))
    }
    
    if (currentProvince === originalValues.province && originalValues.province) {
      batchUpdateAddress({
        city: originalValues.city || '',
        city_id: originalValues.cityId || '',
        postal_code: originalValues.postalCode || ''
      })
      citySearch.updateSearch(originalValues.city || '')
      postalSearch.updateSearch(originalValues.postalCode || '')
      return
    }

    if (initialAddress.province !== provinceQuebec && currentProvince === provinceQuebec) {
      batchUpdateAddress({
        city: '',
        city_id: '',
        postal_code: ''
      })
      citySearch.clearSearch()
      postalSearch.clearSearch()
      return
    }
    
    if (currentProvince !== provinceQuebec && currentProvince !== initialAddress.province) {
      batchUpdateAddress({
        city_id: '',
        city: '',
        postal_code: ''
      })
      citySearch.clearSearch()
      postalSearch.clearSearch()
    }
  }, [
    currentAddress.province, 
    addressState.isInitialized, 
    addressState.originalValues,
    initialAddress.province,
    formik.setValues,
    citySearch.updateSearch,
    citySearch.clearSearch,
    postalSearch.updateSearch,
    postalSearch.clearSearch
  ])

  useEffect(() => {
    if (previousCityIdRef.current && 
        previousCityIdRef.current !== currentAddress.city_id &&
        currentAddress.city_id) {
      postalSearch.clearSearch()
      formik.setFieldValue('address.postal_code', '')
    }
    
    previousCityIdRef.current = currentAddress.city_id
  }, [currentAddress.city_id, formik.setFieldValue, postalSearch.clearSearch])

  const handleCitySearchChange = useCallback((searchValue: string) => {
    citySearch.updateSearch(searchValue)
  }, [citySearch.updateSearch])

  const handleCityChange = useCallback((event: any, newValue: any) => {
    if (newValue) {
      formik.setValues((prev: any) => ({
        ...prev,
        address: {
          ...prev.address,
          city_id: newValue.id,
          city: newValue.name
        }
      }))
    } else {
      formik.setValues((prev: any) => ({
        ...prev,
        address: {
          ...prev.address,
          city_id: '',
          city: ''
        }
      }))
    }
  }, [formik.setValues])

  const handlePostalSearchChange = useCallback((searchValue: string) => {
    const normalizedValue = searchValue.replace(/\s+/g, '')
    postalSearch.updateSearch(normalizedValue)
  }, [postalSearch.updateSearch])

  const handlePostalChange = useCallback((event: any, newValue: any) => {
    if (newValue) {
      formik.setFieldValue('address.postal_code', newValue.name)
    } else {
      formik.setFieldValue('address.postal_code', '')
    }
  }, [formik.setFieldValue])

  return (
    <SectionForm title='adresse'>
      <Stack direction={isMobile ? 'column' : 'row'} spacing='24px' sx={{ mb: '24px' }}>
        <EditableTextField
          label='No de rue'
          value={currentAddress.civic_number}
          required
          sx={{ width: isMobile ? '100%' : '85px' }}
        >
          <NumberFieldFormik name='address.civic_number' placeholder='No de rue' />
        </EditableTextField>
        <EditableTextField
          label='Rue'
          required
          sx={{ width: isMobile ? '100%' : '225px', mt: isMobile ? '24px' : 0 }}
          value={currentAddress.street}
        >
          <OutlinedFieldFormik name='address.street' placeholder='Rue' />
        </EditableTextField>
        <EditableTextField
          label='Bureau, app.'
          value={currentAddress.office}
          sx={{ width: isMobile ? '100%' : '111px', mt: isMobile ? '24px' : 0 }}
        >
          <OutlinedFieldFormik
            name='address.office'
            placeholder='Bureau, app.'
          />
        </EditableTextField>
      </Stack>
      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
        <EditableTextField
          label='Pays'
          required
          value={currentAddress?.country || 'Canada'}
          sx={{ width: isMobile ? '100%' : '334px', mr: isMobile ? 0 : '24px' }}
        >
          <OutlinedFieldFormik disabled name='address.country' placeholder='Pays' />
        </EditableTextField>
        <EditableTextField
          sx={{ width: isMobile ? '100%' : '224px', mt: isMobile ? '24px' : 0 }}
          label='Province'
          value={currentAddress?.province ? t(`provinces.${currentAddress.province}`) : ''}
          required
        >
          <SelectFormik
            name='address.province'
            placeholder='Province'
            fullWidth
            listItems={getProvinces() || []}
          />
        </EditableTextField>
      </Box>

      <Stack direction={isMobile ? 'column' : 'row'} spacing='24px' sx={{ mt: '35px' }}>
        <EditableTextField
          label='Ville'
          required
          value={currentAddress.city}
          sx={{ width: isMobile ? '100%' : '334px' }}
        >
          {currentAddress.province === provinceQuebec ? (
            <BaseAutoComplete
              name='address.city_id'
              placeholder={currentAddress.city || 'Ville'}
              listItems={cities || []}
              minSearchLength={3}
              preserveTypedValue={true}
              autoInitializeValue={currentAddress.city}
              onSearchChange={handleCitySearchChange}
              onChange={handleCityChange}
            />
          ) : (
            <OutlinedFieldFormik 
              name='address.city' 
              placeholder='Ville' 
              fullWidth 
            />
          )}
        </EditableTextField>
        <EditableTextField
          label='Code postal'
          required
          value={currentAddress.postal_code}
          sx={{ width: isMobile ? '100%' : '120px', mt: isMobile ? '24px' : 0 }}
        >
          {currentAddress.province === provinceQuebec ? (
            <BaseAutoComplete
              name='address.postal_code'
              placeholder={currentAddress.postal_code || 'Code postal'}
              listItems={postes || []}
              minSearchLength={5}
              maxSearchLength={7}
              isPostalCode={true}
              onSearchChange={handlePostalSearchChange}
              onChange={handlePostalChange}
            />
          ) : (
            <OutlinedFieldFormik
              name='address.postal_code'
              placeholder='Code postal'
              fullWidth
            />
          )}
        </EditableTextField>
      </Stack>
    </SectionForm>
  )
}

export default AdressForm